import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Award, Shield, Check, Plus, X, Search } from 'lucide-react'

/**
 * Certificates Form Component
 *
 * Allows managing compliance standards (certificates) for the project:
 * - View all available certificates
 * - Add/remove certificates from project
 * - Search/filter available certificates
 */
const CertificatesForm = ({ selectedCertificates, availableCertificates, onChange, disabled }) => {
	const [searchQuery, setSearchQuery] = useState('')

	const handleToggleCertificate = (certId) => {
		if (disabled) return

		const id = parseInt(certId, 10)
		if (selectedCertificates.includes(id)) {
			onChange(selectedCertificates.filter((c) => c !== id))
		} else {
			onChange([...selectedCertificates, id])
		}
	}

	// Convert certificate IDs to numbers for comparison (API returns string IDs)
	const selectedCerts = availableCertificates.filter((c) => selectedCertificates.includes(parseInt(c.id, 10)))
	const availableCerts = availableCertificates.filter((c) => !selectedCertificates.includes(parseInt(c.id, 10)))

	// Filter available certificates based on search query
	const filteredAvailableCerts = useMemo(() => {
		if (!searchQuery.trim()) return availableCerts

		const query = searchQuery.toLowerCase().trim()
		return availableCerts.filter((cert) => {
			const name = (cert.name || cert.title || '').toLowerCase()
			const description = (cert.description || '').toLowerCase()
			return name.includes(query) || description.includes(query)
		})
	}, [availableCerts, searchQuery])

	return (
		<div className="certificates-form">
			<div className="form-section">
				<h3 className="form-section__title">
					<Award size={20} />
					Selected Compliance Standards
				</h3>
				<p className="form-section__description">
					These compliance standards are currently associated with your project.
				</p>

				{selectedCerts.length > 0 ? (
					<div className="certificates-form__selected">
						{selectedCerts.map((cert) => (
							<div key={cert.id} className="certificate-card certificate-card--selected">
								<div className="certificate-card__checkbox">
									<Check size={14} />
								</div>
								<div className="certificate-card__content">
									<h4 className="certificate-card__name">{cert.name || cert.title}</h4>
								</div>
								{!disabled && (
									<button
										type="button"
										className="certificate-card__remove"
										onClick={() => handleToggleCertificate(cert.id)}
										title="Remove from project"
									>
										<X size={16} />
									</button>
								)}
							</div>
						))}
					</div>
				) : (
					<div className="certificates-form__empty">
						<Shield size={40} />
						<p>No compliance standards selected for this project.</p>
						<span>Select from the available standards below.</span>
					</div>
				)}
			</div>

			<div className="form-section">
				<h3 className="form-section__title">
					<Plus size={20} />
					Available Compliance Standards
				</h3>
				<p className="form-section__description">
					Click on a standard to add it to your project.
				</p>

				{/* Search Filter */}
				{availableCerts.length > 0 && (
					<div className="certificates-form__search">
						<Search size={18} className="certificates-form__search-icon" />
						<input
							type="text"
							className="certificates-form__search-input"
							placeholder="Search compliance standards..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
						{searchQuery && (
							<button
								type="button"
								className="certificates-form__search-clear"
								onClick={() => setSearchQuery('')}
								title="Clear search"
							>
								<X size={16} />
							</button>
						)}
					</div>
				)}

				{filteredAvailableCerts.length > 0 ? (
					<div className="certificates-form__available">
						{filteredAvailableCerts.map((cert) => (
							<div
								key={cert.id}
								className={`certificate-card certificate-card--available ${disabled ? 'disabled' : ''}`}
								onClick={() => handleToggleCertificate(cert.id)}
								role="button"
								tabIndex={disabled ? -1 : 0}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										handleToggleCertificate(cert.id)
									}
								}}
							>
								<div className="certificate-card__checkbox certificate-card__checkbox--empty">
									<Plus size={12} />
								</div>
								<div className="certificate-card__content">
									<h4 className="certificate-card__name">{cert.name || cert.title}</h4>
								</div>
							</div>
						))}
					</div>
				) : availableCerts.length > 0 && searchQuery ? (
					<div className="certificates-form__empty certificates-form__empty--no-results">
						<Search size={40} />
						<p>No standards match "{searchQuery}"</p>
						<button
							type="button"
							className="btn btn--outline btn--sm"
							onClick={() => setSearchQuery('')}
						>
							Clear Search
						</button>
					</div>
				) : (
					<div className="certificates-form__empty certificates-form__empty--all-selected">
						<Check size={40} />
						<p>All available standards have been selected.</p>
					</div>
				)}
			</div>
		</div>
	)
}

CertificatesForm.propTypes = {
	selectedCertificates: PropTypes.arrayOf(PropTypes.number).isRequired,
	availableCertificates: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			name: PropTypes.string,
			title: PropTypes.string,
			description: PropTypes.string,
		})
	).isRequired,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
}

CertificatesForm.defaultProps = {
	disabled: false,
}

export default CertificatesForm
